import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getCurrentPage,
    getLocation,
    removeAbsoluteUrl,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import SignInResetPasswordForm, {
    signInResetPasswordFormStyles,
} from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    CallbackType,
    ErrorObjectType,
    InputObjectType,
    validate,
    ValidatorRuleType,
} from "@tKgpco/Common/Utilities/validate";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    changePasswordInstructions = "changePasswordInstructions",
    formAction = "formAction",
    hiddenUrl = "hiddenUrl",
    forgotPasswordUrl = "forgotPasswordUrl",
    fullWidthButton = "fullWidthButton",
}

const intialErrorFormState = {
    user_code: "",
    PASSWORD: "",
};

interface ValidatorConfigType {
    [key: string]: ValidatorRuleType;
}

interface FormErrorsStateType {
    [key: string]: string | undefined;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.changePasswordInstructions]: string;
        [fields.formAction]: string;
        [fields.forgotPasswordUrl]: string;
        [fields.hiddenUrl]: string;
        [fields.fullWidthButton]: boolean;
    };
}

const signInValidatorConfig: ValidatorConfigType = {
    user_code: {
        required: true,
        minLength: 2,
        label: "User Name",
    },
    PASSWORD: {
        required: true,
        minLength: 5,
        label: "Password",
    },
};

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ returnUrl?: string; externalError?: string }>(location.search);
    const pageType = getCurrentPage(state).type;
    const isSignInPage = pageType === "SignInPage";
    const isConfirmationPage = pageType === "OrderConfirmationPage" || pageType === "OnePageCheckoutConfirmationPage";
    const returnUrl = isSignInPage
        ? removeAbsoluteUrl(parsedQuery.returnUrl)
        : `${location.pathname}${location.search}`;

    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        returnUrl,
        externalError: parsedQuery.externalError,
        properties: state.context.settings.properties,
        isConfirmationPage,
        homePageUrl: getHomePageUrl(state),
    };
};

// eslint-disable-next-line no-use-before-define
type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

const mapDispatchToProps = {
    signIn,
};

export interface SignInExistingAccountStyles {
    form?: InjectableCss;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: GridItemProps;
    rememberMeGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    signInExistingAccountTitle?: TypographyProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    changePasswordModal?: ModalPresentationProps;
    container?: InjectableCss;
    hiddenWrap?: InjectableCss;
}

export const signInExistingAccountStyles: SignInExistingAccountStyles = {
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    signInExistingAccountTitle: {
        variant: "h4",
        css: css`
            // background: #0b70b7;
            background-color: ${COLORS.primary.light};
            padding: 20px;
            color: #ffffff;
            display: flex;
            margin-bottom: 0;
            justify-content: center;
            font-weight: ${TYPOGRAPHY.weight.bold};
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
        `,
    },
    userNameGridItem: {
        width: 12,
        css: css`
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
                font-weight: ${TYPOGRAPHY.weight.bold};
            }
        `,
    },
    userNameTextField: {
        cssOverrides: {
            formInputWrapper: css`
                > div {
                    margin-top: 0px !important;
                }
            `,
        },
        css: css`
            border-radius: 4px !important;
            // the following css is to given for the iphone specific issue
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.base} !important;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    passwordGridItem: {
        width: 12,
        css: css`
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
                font-weight: ${TYPOGRAPHY.weight.bold};
            }
            padding-bottom: 0;
        `,
    },
    passwordTextField: {
        cssOverrides: {
            formInputWrapper: css`
                > div {
                    margin-top: 0px !important;
                }
            `,
        },
        css: css`
            border-radius: 4px !important;
            // the following css is to given for the iphone specific issue
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.base} !important;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    signInButtonGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            justify-content: flex-start;
        `,
    },
    rememberMeGridItem: {
        width: 7,
        css: css`
            padding: 0 15px 15px 15px;
            align-items: center;
        `,
    },
    rememberMeCheckbox: {
        css: css`
            span[role="checkbox"] {
                background: #fff;
                min-width: 18px;
                min-height: 18px;
            }
        `,
    },
    forgotPasswordGridItem: {
        width: 5,
        css: css`
            justify-content: flex-end;
            align-items: center;
        `,
    },
    forgotPasswordLink: {
        typographyProps: {
            size: 14,
            color: COLORS.link.base,
            weight: TYPOGRAPHY.weight.bold,
        },
    },
    signInButton: {
        variant: "primary",
        typographyProps: {
            size: TYPOGRAPHY.size.small,
            weight: TYPOGRAPHY.weight.bold,
        },
        css: css`
            text-transform: uppercase;
            border-radius: 22px;
            background-color: ${COLORS.link.base};
            background-image: linear-gradient(to right, #44a8f7, ${COLORS.link.base});
            border: none;
            width: 120px;
            color: ${COLORS.white};
            &:disabled {
                background: ${COLORS.disabled.base};
            }
            &:hover {
                background-color: ${COLORS.link.base};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 173px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
    container: {
        css: css`
            background: #f1f6fb;
            border: 1px solid #e1e7ed;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
            padding: 50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 20px;
                            height: auto;
                            min-height: 0;
                        `,
                    ],
                    "max",
                )}
            padding-bottom: 20px;
            min-height: 385px;
        `,
    },
};

signInResetPasswordFormStyles.sendButton = signInExistingAccountStyles.signInButton;

signInResetPasswordFormStyles.cancelButton = {
    // color: "tertiary",
    typographyProps: {
        size: TYPOGRAPHY.size.smaller,
        weight: TYPOGRAPHY.weight.bold,
    },
    css: css`
        margin-right: 10px;
        // text-transform: uppercase;
        border-radius: 22px;
        background-color: #e1e7ed;
        border: none;
        color: ${COLORS.lightBlack};

        &:hover {
            background-color: #e1e7ed;
        }
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        width: 100%;
                    `,
                ],
                "max",
            )}
    `,
};

signInResetPasswordFormStyles.descriptionText = {
    size: 14,
};

/**
 * @deprecated Use signInExistingAccountStyles instead.
 */
export const signInExistingAccount = signInExistingAccountStyles;
const styles = signInExistingAccountStyles;
const StyledForm = getStyledWrapper("form");

const SignInExistingAccount = ({
    signIn,
    fields,
    accountSettings,
    returnUrl,
    externalError,
    properties,
    isConfirmationPage,
    homePageUrl,
    theme,
}: Props) => {
    const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;
    const legacyUserEmailDomain = properties.legacyUserEmailDomain;
    const [rememberMe, setRememberMe] = useState(false);
    const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [userNameElement, setUserNameElement] = React.useState<HTMLInputElement>();
    const [passwordElement, setPasswordElement] = React.useState<HTMLInputElement>();
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || "");
    const [formErrors, setFormErrors] = React.useState<FormErrorsStateType>(intialErrorFormState);
    const showRememberMe = accountSettings.rememberMe;
    const showKeepMeSignedIn = accountSettings.keepMeSignedIn;

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;
        setIsSignInClicked(true);
        if (!userName || !pass) {
            return;
        }

        if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
            return;
        }

        setErrorMessage("");

        signIn({
            userName,
            password: pass,
            rememberMe,
            keepMeSignedIn,
            returnUrl: theme.signIn?.useOverlay && isConfirmationPage ? homePageUrl : returnUrl,
            skipRedirect: theme.signIn?.useOverlay && !isConfirmationPage,
            onError: (error, statusCode) => {
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                }

                setErrorMessage(error);
            },
        });
    };

    /**
     * Check if Domain given is properties is present in email and
     * if domain is present then system will redirect to legacy website
     *
     */

    const emailValidationCheck = () => {
        const isValidFrontTEmail: string[] = [];

        if (legacyUserEmailDomain?.trim().length) {
            legacyUserEmailDomain.split(",").forEach((domain: string) => {
                if (userName.toLowerCase().indexOf(domain.toLowerCase().trim()) !== -1) {
                    isValidFrontTEmail.push(domain);
                }
            });
        }
        return isValidFrontTEmail;
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        // regex to validate userName contains email or not
        const isValidEmail = userName.match(regex);

        const cloneFormErrors = { ...formErrors };
        for (const [key, value] of Object.entries(cloneFormErrors)) {
            const element: HTMLInputElement | null = document.querySelector(`[name="${key}"]`);
            if (element) {
                validateHandler(element);
            }
        }

        if ((Object.entries(formErrors).length === 0 && isValidEmail === null) || emailValidationCheck().length > 0) {
            return true;
        }
        if (formErrors.user_code || formErrors.PASSWORD) {
            // window.location.href=siteMessage("Old_Redirect_URL").toString();
            event.preventDefault();
            return false;
        }
        event.preventDefault();
        signInHandler();
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? siteMessage("SignInInfo_Email_Required")
                    : siteMessage("SignInInfo_UserName_Required");
            }
        }

        return "";
    };

    const onBlurValidationHandler = (input: HTMLInputElement) => {
        validateHandler(input);
    };

    const validateHandler = (input: HTMLInputElement, customValidation?: CallbackType): void => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };

        const validatorConfig = signInValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);
        if (isValidate === undefined) {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(removeKey(formErrors, input.name));
        } else {
            setFormErrors(prevState => {
                return {
                    ...prevState,
                    [input.name]: isValidate ? isValidate.errorMsg : isValidate,
                };
            });
        }
        if (!validatorConfig.required && input.value === "") {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        }
    };

    const removeKey = (formErrorObject: FormErrorsStateType, deleteKey: string) => {
        return Object.assign(
            {},
            ...Object.entries(formErrorObject)
                .filter(([k]) => k !== deleteKey)
                .map(([k, v]) => ({ [k]: v })),
        );
    };

    const forgotPasswordLinkClick = () => {
        // Access the user name element using below line to get name and value from element
        const element: HTMLSelectElement | HTMLInputElement | any = document.querySelector(`[name="user_code"]`);
        const inputObject: InputObjectType = {
            name: element.name,
            value: element.value,
        };

        // regex to check whether user entered Email Id or username
        const isValidEmail = element.value.match(regex);

        // Validation of error on page if exists on clicking on forgot password link
        const validatorConfig = signInValidatorConfig[element.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig);

        // Setting up error messages in state of component
        if (isValidate === undefined) {
            delete formErrors[element.name as keyof FormErrorsStateType];
            setFormErrors(removeKey(formErrors, element.name));
        } else {
            setFormErrors(prevState => {
                return {
                    ...prevState,
                    [element.name]: isValidate ? isValidate.errorMsg : isValidate,
                };
            });
        }

        if (!validatorConfig.required && element.value === "") {
            delete formErrors[element.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        }

        /**
         * If user enters valid email the reset password popup will open,
         * Otherwise will redirected to legacy forgot password URL.
         * */
        if (isValidEmail && emailValidationCheck().length === 0) {
            setIsResetPasswordModalOpen(true);
        } else if (!isValidate || emailValidationCheck().length > 0) {
            window.open(`${fields.forgotPasswordUrl}`, "_self");
        }
    };

    React.useEffect(() => {
        const cloneFormErrors = { ...formErrors };
        for (const [key, value] of Object.entries(cloneFormErrors)) {
            const element: HTMLSelectElement | HTMLInputElement | null = document.querySelector(`[name="${key}"]`);
            if (element && element.value) {
                delete cloneFormErrors[key as keyof FormErrorsStateType];
                setFormErrors(removeKey(cloneFormErrors, key));
            }
        }
    }, []);

    const userErrorMessage = validateUserName();
    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    const signInButtonCss = css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    width: 100%;
                `,
                css`
                    width: 100%;
                `,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
            ])}
    `;

    return (
        <>
            <Typography {...styles.signInExistingAccountTitle}>{translate("Already have an account?")}</Typography>
            {/* Adapty Custom Code */}
            <StyledWrapper {...styles.container}>
                <StyledForm
                    name="login"
                    action={fields.formAction}
                    method="post"
                    {...styles.form}
                    onSubmit={submitHandler}
                    noValidate
                >
                    <GridContainer {...styles.signInGridContainer}>
                        <GridItem {...styles.hiddenWrap}>
                            <TextField value={fields.hiddenUrl} name="target" type="hidden" />
                            <TextField value={0} name="SMAUTHREASON" type="hidden" />
                        </GridItem>
                        <GridItem {...styles.userNameGridItem}>
                            <TextField
                                id="userName"
                                name="user_code"
                                {...styles.userNameTextField}
                                label={
                                    accountSettings.useEmailAsUserName
                                        ? translate("Username/Email")
                                        : translate("User Name")
                                }
                                placeholder={translate("Enter User Name")}
                                onChange={e => {
                                    setUserName(e.currentTarget.value);
                                    setUserNameElement(e.currentTarget);
                                    validateHandler(e.currentTarget);
                                }}
                                onBlur={e => onBlurValidationHandler(e.currentTarget)}
                                value={userName}
                                error={!!formErrors.user_code && formErrors.user_code}
                                autoComplete="username"
                                data-test-selector="signIn_userName"
                            />
                        </GridItem>
                        <GridItem {...styles.passwordGridItem}>
                            <TextField
                                id="password"
                                name="PASSWORD"
                                {...styles.passwordTextField}
                                label={translate("Password")}
                                placeholder={translate("Enter your Password")}
                                onChange={e => {
                                    setPassword(e.currentTarget.value);
                                    setPasswordElement(e?.currentTarget);
                                    validateHandler(e.currentTarget);
                                }}
                                value={password}
                                error={formErrors.PASSWORD ? formErrors.PASSWORD : passwordErrorMessage}
                                onBlur={e => onBlurValidationHandler(e.currentTarget)}
                                /* Adapty Custom Code */
                                // iconProps={{ ...styles.icon, src: showPassword ? EyeOff : Eye }}
                                // iconClickableProps={{
                                //     onClick: () => {
                                //         setShowPassword(!showPassword);
                                //     },
                                //     type: "button",
                                // }}
                                /* Adapty Custom Code */
                                clickableText={password}
                                autoComplete="current-password"
                                data-test-selector="signIn_password"
                                type={showPassword ? "text" : "password"}
                            />
                        </GridItem>
                        <GridItem {...styles.rememberMeGridItem}>
                            {showRememberMe && (
                                <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                    <Checkbox
                                        uid="rememberMe"
                                        {...styles.rememberMeCheckbox}
                                        checked={rememberMe}
                                        onChange={(_, value) => setRememberMe(value)}
                                    >
                                        {translate("Remember Me")}
                                    </Checkbox>
                                </CheckboxGroup>
                            )}
                            {showKeepMeSignedIn && (
                                <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                    <Checkbox
                                        uid="keepMeSignedIn"
                                        {...styles.rememberMeCheckbox}
                                        checked={keepMeSignedIn}
                                        onChange={(_, value) => setKeepMeSignedIn(value)}
                                    >
                                        {translate("Keep Me Signed In")}
                                    </Checkbox>
                                </CheckboxGroup>
                            )}
                        </GridItem>
                        <GridItem {...styles.forgotPasswordGridItem}>
                            <Link
                                {...styles.forgotPasswordLink}
                                type="button"
                                onClick={forgotPasswordLinkClick}
                                data-test-selector="signIn_forgotPassword"
                            >
                                {translate("Forgot Password?")}
                            </Link>
                            <Modal
                                headline={translate("Reset Password")}
                                {...styles.resetPasswordModal}
                                isOpen={isResetPasswordModalOpen}
                                handleClose={() => onResetPasswordClose()}
                                data-test-selector="resetPasswordModal"
                            >
                                <SignInResetPasswordForm onClose={() => onResetPasswordClose()} />
                            </Modal>
                        </GridItem>
                        <GridItem {...styles.signInButtonGridItem}>
                            <Button
                                {...styles.signInButton}
                                type="submit"
                                data-test-selector="signIn_submit"
                                data-content-category="Ecommerce"
                                data-content-tracking="Sign In"
                            >
                                {translate("Sign In")}
                            </Button>
                            <Modal
                                headline={translate("Change Password")}
                                {...styles.changePasswordModal}
                                isOpen={isChangePasswordModalOpen}
                                handleClose={onChangePasswordClose}
                            >
                                <SignInChangePasswordForm
                                    enteredUserName={userName}
                                    enteredPassword={password}
                                    instructionsText={fields.changePasswordInstructions}
                                    onPasswordChanged={onPasswordChanged}
                                />
                            </Modal>
                        </GridItem>
                    </GridContainer>
                </StyledForm>
            </StyledWrapper>
            {/* Adapty Custom Code */}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(SignInExistingAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "LogIn",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.changePasswordInstructions,
                editorTemplate: "RichTextField",
                displayName: "Change Password Instructions",
                defaultValue: "A password change is required for your account before you may continue.",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                sortOrder: 0,
            },
            {
                name: fields.formAction,
                fieldType: "Translatable",
                editorTemplate: "MultilineTextField",
                displayName: "Form Action URL",
                isRequired: true,
                defaultValue: "",
            },
            {
                name: fields.hiddenUrl,
                fieldType: "Translatable",
                editorTemplate: "MultilineTextField",
                displayName: "Hidden URL",
                isRequired: true,
                defaultValue: "",
            },
            {
                name: fields.forgotPasswordUrl,
                fieldType: "Translatable",
                editorTemplate: "MultilineTextField",
                displayName: "Forgot Password URL",
                isRequired: true,
                defaultValue: "",
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
